import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check } from 'lucide-react';
import Layout from '../components/layout/Layout';
import ServiceIcon from '../components/ServiceIcon';
import { Button } from '../components/ui/button';
import { services, blogPosts } from '../data/mock';
import SEOHead from '../components/seo/SEOHead';
import { ServiceSchema, BreadcrumbSchema, OrganizationSchema, FAQSchema } from '../components/seo/StructuredData';
import { serviceSeo } from '../data/routeSeo';

const ServiceDetail = () => {
  const { id } = useParams();
  const service = services.find(s => s.id === id);
  const otherServices = services.filter(s => s.id !== id);

  // Get related blog posts based on the service's SEO tag or category
  const getRelatedPosts = () => {
    if (!service) return [];
    const matching = blogPosts.filter(
      (p) =>
        p.tags.includes(service.seoTag) ||
        p.category === service.type ||
        p.title.toLowerCase().includes(service.seoTag.toLowerCase())
    );
    const fallback = matching.length > 0 ? matching : blogPosts.filter((p) => p.author.name === 'Tolulope Omotosho');
    return fallback.slice(0, 2);
  };

  const relatedPosts = getRelatedPosts();

  // Service-specific FAQs
  const serviceFaqs = [
    {
      question: `How quickly will I see results from ${service?.title}?`,
      answer: `Most clients go live within 7–14 days and see the impact immediately — every enquiry answered instantly, every booking confirmed. We focus on quick wins while building a system that keeps improving month after month.`
    },
    {
      question: `What's included in the ${service?.pricing} package?`,
      answer: `Our packages include everything listed above, plus done-for-you setup, AI training on your business, dedicated support on WhatsApp, and monthly performance reports. No long-term contracts — we earn your business every month.`
    }
  ];

  if (!service) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Service Not Found</h1>
            <Link to="/services">
              <Button>View All Services</Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEOHead {...serviceSeo(service)} />
      <ServiceSchema service={service} />
      <OrganizationSchema />
      <BreadcrumbSchema items={[
        { name: 'Home', path: '/' },
        { name: 'Services', path: '/services' },
        { name: service.title, path: `/services/${service.id}` }
      ]} />
      <FAQSchema faqItems={serviceFaqs} />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: `${service.color}10` }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/services" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Services
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div
                className="w-20 h-20 rounded-2xl flex items-center justify-center mb-8"
                style={{ backgroundColor: `${service.color}20` }}
              >
                <div style={{ color: service.color }}>
                  <ServiceIcon icon={service.icon} className="w-8 h-8" />
                </div>
              </div>

              <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                {service.title}
              </h1>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                {service.description}
              </p>
              <p className="text-2xl font-bold text-white mb-2">
                {service.pricing}
              </p>
              {service.setupFee && (
                <p className="text-base text-gray-400 mb-8">{service.setupFee}</p>
              )}
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-8 py-6 text-lg rounded-xl"
                >
                  Get Started Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-amber-500/20 rounded-3xl blur-2xl" />
              <img
                src={service.image}
                alt={service.title}
                className="relative rounded-3xl w-full aspect-[4/3] object-cover shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="py-20 bg-red-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Does This Sound Familiar?
            </h2>
            <p className="text-lg text-gray-600">
              If you're experiencing any of these, you're not alone—and we can help.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {service.painPoints.map((pain, index) => (
              <div
                key={index}
                className="flex items-start gap-4 p-6 bg-white rounded-xl border border-red-200"
              >
                <span className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-red-500 font-bold">{index + 1}</span>
                </span>
                <p className="text-gray-700 font-medium">{pain}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              What's Included
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need, done for you — setup, training and ongoing support.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {service.features.map((feature, index) => (
              <div
                key={index}
                className="flex items-start gap-3 p-6 bg-gray-50 rounded-xl"
              >
                <Check className="w-6 h-6 text-emerald-500 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              The Results You Can Expect
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {service.benefits.map((benefit, index) => (
              <div
                key={index}
                className="flex items-center gap-4 p-6 bg-white rounded-xl shadow-sm"
              >
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Check className="w-6 h-6 text-emerald-600" />
                </div>
                <span className="text-lg text-gray-900 font-semibold">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Blog Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-gray-900">Related Articles</h2>
              <Link to="/blog" className="text-emerald-600 font-medium hover:text-emerald-700 flex items-center gap-1">
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {relatedPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="flex gap-6 p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors group"
                >
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-32 h-24 rounded-lg object-cover flex-shrink-0"
                  />
                  <div>
                    <span className="text-emerald-600 text-sm font-medium">{post.category}</span>
                    <h3 className="font-bold text-gray-900 mt-1 group-hover:text-emerald-600 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-sm text-gray-500 mt-2">{post.readTime}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Book a free 15-minute call — we'll show you exactly how this would work for
            your business, with a live demo trained on your services.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-10 py-6 text-lg rounded-xl"
            >
              Schedule Free Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Other Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Explore Our Other Services
          </h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {otherServices.map((s) => {
              return (
                <Link
                  key={s.id}
                  to={`/services/${s.id}`}
                  className="flex items-start gap-4 p-6 bg-white rounded-xl border border-gray-200 hover:border-emerald-300 hover:shadow-lg transition-all group"
                >
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${s.color}15` }}
                  >
                    <div style={{ color: s.color }}>
                      <ServiceIcon icon={s.icon} className="w-7 h-7" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 group-hover:text-emerald-600 transition-colors">
                      {s.title}
                    </h3>
                    <p className="text-sm text-gray-500">{s.shortDesc}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ServiceDetail;
